import { PlusOutlined } from '@ant-design/icons'
import { CarePlan, Carer, RiskAssessment, CarePlanAdl } from '@lmc/ts-common'
import { Button, Modal, Select } from '@lmc/ui-kit'
import { getJSON } from 'common/services/api'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import CarePlanItem, { CarePlanItemBaseProps } from './CarePlanItem'
import useCarers, { getCarerName } from 'hooks/useFindItem/useCarers'
import { useSelector } from 'react-redux'
import { Text } from '@lmc/ui-kit/src/general'
import { CarePlansInputProps, transformCarePlanAdlIntoPlan } from '../../utils'

const CarePlansInput = (props: CarePlansInputProps) => {
  const { value, onChange, disabled } = props

  const safeValue = value || []

  const [isModalVisible, setIsModalVisible] = useState(false)
  const { t } = useTranslation(NAMESPACE.GOALS)
  const preferredCarePlansNominative:
    | 'Care Plans'
    | 'Support Plans'
    | undefined = useSelector(
    (state: any) => state.global.home.preferredCarePlansNominative
  )
  const carePlansNominative = preferredCarePlansNominative || 'Care Plans'
  const { id: residentID } = useParams()

  const { data } = useQuery<{ result: CarePlan[] }>('care-plans', () =>
    getJSON({
      url: `/api/list/care-plans?resident=${residentID}&isRevision=false`,
    })
  )
  const [selectedPlans, setSelectedPlans] = useState<Plan[]>(safeValue)

  useEffect(() => {
    setSelectedPlans([...safeValue])
  }, [value])

  const options = getOptions(data?.result)
  const canAddCarePlans = options && options?.length > 0

  const addHandler = () => {
    setIsModalVisible(true)
  }

  const confirmHandler = () => {
    onChange?.(selectedPlans)

    setIsModalVisible(false)
  }

  const userID: string = useSelector((state: any) => state.global.user.id)
  const { result: userCarer } = useCarers({ id: userID })
  const userName = userCarer ? getCarerName(userCarer as Carer) : ''

  const onSelectChange = (options: CarePlan[]) => {
    const newSelectedValues = options.map((option) =>
      transformCurrentAddedCarePlanIntoPlan(option, userName)
    )

    setSelectedPlans([...newSelectedValues])
  }
  const unlinkHandler = (id: string) => {
    const newValue = safeValue.filter((element) => element.id !== id)

    onChange?.(newValue)
  }

  const { data: allRiskAssesmentsData, isLoading } = useQuery<{
    result: RiskAssessment[]
  }>('risk-assesments', () => getJSON({ url: '/api/list/risk-assessments' }))

  const allRiskAssesment: RiskAssessment[] = useMemo(
    () => allRiskAssesmentsData?.result || [],
    [allRiskAssesmentsData]
  )

  return (
    <Wrapper>
      <Modal
        noPadding
        localisation={{
          title: t(goalsConstants[`SELECT_${carePlansNominative}`]),
          confirmText: t(goalsConstants.CONFIRM),
          cancelText: t(goalsConstants.CANCEL),
        }}
        onCancel={() => setIsModalVisible(false)}
        onConfirm={() => confirmHandler()}
        isModalVisible={isModalVisible}
      >
        <Select<string[], CarePlan>
          defaultValue={safeValue.map((val) => val.id)}
          style={{ width: '100%' }}
          options={options}
          mode="multiple"
          onChange={(value, options) => onSelectChange(options as CarePlan[])}
          placeholder={t(goalsConstants[carePlansNominative])}
        />
      </Modal>
      <ListWrapper>
        {safeValue.map((itemProps) => (
          <CarePlanItem
            disabled={disabled}
            key={itemProps.id}
            isLoading={isLoading}
            allRiskAssesments={allRiskAssesment}
            onUnlink={unlinkHandler}
            {...itemProps}
          />
        ))}
      </ListWrapper>
      {!disabled && (
        <>
          <ButtonStyled
            disabled={!canAddCarePlans}
            type="button"
            icon={<PlusOutlined />}
            variant="text"
            onClick={addHandler}
          >
            {t(goalsConstants[`LINK_${carePlansNominative}`])}
          </ButtonStyled>
          {!canAddCarePlans && (
            <Text level={2}>
              {t(goalsConstants[`NO_${carePlansNominative}`])}
            </Text>
          )}
        </>
      )}
    </Wrapper>
  )
}

const getCarePlanName = (carePlan: CarePlan) =>
  carePlan.customName || (carePlan.carePlanAdl as CarePlanAdl | null)?.name

const getOptions = (carePlans: CarePlan[] | undefined) =>
  carePlans
    ?.map((carePlan) => ({
      ...carePlan,
      value: carePlan.id,
      label: getCarePlanName(carePlan),
    }))
    .filter(
      (carePlan) =>
        getCarePlanName(carePlan) && carePlan.noFurtherSupportRequired === false
    )

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const ListWrapper = styled.div``

const ButtonStyled = styled(Button)`
  position: absolute;
  top: -30px;
  right: 0;
  border: none;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    position: static;
  }
`

export type Plan = Omit<
  CarePlanItemBaseProps,
  'allRiskAssesments' | 'isLoading'
>

const transformCurrentAddedCarePlanIntoPlan = (
  carePlan: CarePlan,
  currentUserName: string
): Plan => {
  const adl: CarePlanAdl = carePlan.carePlanAdl as CarePlanAdl

  const newSelectedPlan: Plan = {
    ...transformCarePlanAdlIntoPlan(adl),
    id: carePlan.id,
    // this is a Date string
    // wrong type used
    linkedDateTime: new Date().toISOString(),
    linkedBy: currentUserName,
    riskAssesmentsIDs: carePlan.riskAssessments as string[],
    nextReviewDate: carePlan.nextReviewDate as string | undefined,
  }

  return newSelectedPlan
}

export default CarePlansInput
